import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { db } from '../firebase';
import ProfileHeader from '../components/ProfileHeader';
import { getOrganizationMemberships } from '../utils/organizationContext';
import { getPreferredOrganizationId } from '../utils/organizationSelection';
import {
  getInstructorPublicProfile,
  upsertInstructorPublicProfile,
} from '../utils/publicProfiles';

// Pass 88.3 §8 — resolve the signed-in user's CANONICAL teaching membership. The instructor public-profile
// identity is the organizationMember BUSINESS userId (member.userId) — NEVER the Firebase Auth uid. The Auth
// uid is only the current-viewer/authorization identity. Returns the member (with its userId + organizationId)
// or null when the signed-in user is not a canonical teaching instructor.
function resolveTeachingMembership(userData = {}) {
  const memberships = getOrganizationMemberships(userData || {}) || [];
  const teaching = memberships.filter((m) => m && m.isTeachingInstructor && m.userId);
  if (!teaching.length) return null;
  const preferred = getPreferredOrganizationId();
  return teaching.find((m) => m.organizationId === preferred)
    || teaching.find((m) => m.organizationId === userData?.orgId)
    || teaching[0];
}

function arrayToText(values = []) {
  return (values || []).join(', ');
}

function textToArray(value = '') {
  return String(value || '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function InstructorPublicProfilePage() {
  const { currentUser, userData } = useAuth();
  const member = useMemo(() => resolveTeachingMembership(userData || {}), [userData]);
  const instructorUserId = member?.userId || '';       // canonical BUSINESS identity (never the Auth uid)
  const instructorOrgId = member?.organizationId || '';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    publicSlug: '',
    displayName: '',
    shortBio: '',
    bio: '',
    disciplinesText: '',
    specialtiesText: '',
    languagesText: '',
    certificationsText: '',
    yearsExperience: '0',
    publicTagsText: '',
    profileStatus: 'draft',
    isPublic: false,
  });

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      try {
        // Load by the CANONICAL member business userId (NOT currentUser.uid).
        const profile = (await getInstructorPublicProfile(db, instructorUserId, { ...userData, ...member })) || {};
        setForm({
          publicSlug: profile.publicSlug || '',
          displayName: profile.displayName || member?.name || userData?.name || '',
          shortBio: profile.shortBio || '',
          bio: profile.bio || '',
          disciplinesText: arrayToText(profile.disciplines),
          specialtiesText: arrayToText(profile.specialties),
          languagesText: arrayToText(profile.languages),
          certificationsText: arrayToText(profile.certifications),
          yearsExperience: String(profile.yearsExperience || 0),
          publicTagsText: arrayToText(profile.publicTags),
          profileStatus: profile.profileStatus || 'draft',
          isPublic: Boolean(profile.isPublic),
        });
      } finally {
        setLoading(false);
      }
    };

    if (instructorUserId) {
      loadProfile();
    } else {
      setLoading(false);
    }
  }, [instructorUserId, userData, member]);

  const publicPath = form.publicSlug ? `/instructors/${form.publicSlug}` : '';

  const handleSave = async () => {
    if (saving || !instructorUserId) return;
    setSaving(true); setMessage(''); setError('');
    try {
      const profile = (await upsertInstructorPublicProfile(db, {
        currentUserId: currentUser?.uid || '',
        userData,
        instructor: {
          ...member,
          id: instructorUserId,
          userId: instructorUserId,
          organizationId: instructorOrgId,
        },
        instructorUserId,
        isSelf: true, // the resolved membership is the signed-in user's OWN canonical teaching membership
        profile: {
          publicSlug: form.publicSlug,
          displayName: form.displayName,
          shortBio: form.shortBio,
          bio: form.bio,
          disciplines: textToArray(form.disciplinesText),
          specialties: textToArray(form.specialtiesText),
          languages: textToArray(form.languagesText),
          certifications: textToArray(form.certificationsText),
          yearsExperience: Number(form.yearsExperience || 0),
          publicTags: textToArray(form.publicTagsText),
          profileStatus: form.profileStatus,
          isPublic: form.isPublic,
        },
      })) || {};
      // Canonical reload: re-read the saved doc so the "saved" state reflects Firestore, not local form.
      const reloaded = (await getInstructorPublicProfile(db, instructorUserId, { ...userData, ...member })) || profile || {};
      setForm({
        publicSlug: reloaded.publicSlug || profile.publicSlug || '',
        displayName: reloaded.displayName || '',
        shortBio: reloaded.shortBio || '',
        bio: reloaded.bio || '',
        disciplinesText: arrayToText(reloaded.disciplines),
        specialtiesText: arrayToText(reloaded.specialties),
        languagesText: arrayToText(reloaded.languages),
        certificationsText: arrayToText(reloaded.certifications),
        yearsExperience: String(reloaded.yearsExperience || 0),
        publicTagsText: arrayToText(reloaded.publicTags),
        profileStatus: reloaded.profileStatus || 'draft',
        isPublic: Boolean(reloaded.isPublic),
      });
      setMessage('Public instructor profile saved.');
      setError('');
    } catch (nextError) {
      console.error(nextError);
      setError('Could not save the public instructor profile right now.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl p-4">
      <ProfileHeader />
      <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Public Profile</p>
            <h1 className="mt-2 text-2xl font-semibold text-slate-950">Instructor Public Profile</h1>
            <p className="mt-2 text-sm text-slate-600">
              Publish a lightweight public profile so prospective clients can discover your specialties and background.
            </p>
          </div>
          {publicPath && form.isPublic && form.profileStatus === 'published' ? (
            <Link className="text-sm text-blue-600 hover:underline" to={publicPath}>
              View Public Page
            </Link>
          ) : null}
        </div>

        {loading ? <p className="mt-4 text-sm text-slate-600">Loading public profile...</p> : null}
        {!loading && !instructorUserId ? (
          <p data-testid="not-instructor" className="mt-4 text-sm text-amber-700">
            Your account is not a canonical teaching instructor in this organisation, so there is no instructor
            public profile to edit. If you expect one, accept your organisation invitation or contact an admin.
          </p>
        ) : null}
        {message ? <p data-testid="profile-saved" className="mt-4 text-sm text-emerald-700">{message}</p> : null}
        {error ? <p data-testid="profile-error" className="mt-4 text-sm text-rose-600">{error}</p> : null}

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <input value={form.displayName} onChange={(event) => setForm((current) => ({ ...current, displayName: event.target.value }))} placeholder="Display name" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
          <input value={form.publicSlug} onChange={(event) => setForm((current) => ({ ...current, publicSlug: event.target.value }))} placeholder="Public slug" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
          <input value={form.shortBio} onChange={(event) => setForm((current) => ({ ...current, shortBio: event.target.value }))} placeholder="Short bio" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm md:col-span-2" />
          <textarea value={form.bio} onChange={(event) => setForm((current) => ({ ...current, bio: event.target.value }))} placeholder="Full bio" rows={5} className="rounded-2xl border border-slate-200 px-4 py-3 text-sm md:col-span-2" />
          <input value={form.disciplinesText} onChange={(event) => setForm((current) => ({ ...current, disciplinesText: event.target.value }))} placeholder="Disciplines, comma separated" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
          <input value={form.specialtiesText} onChange={(event) => setForm((current) => ({ ...current, specialtiesText: event.target.value }))} placeholder="Specialties, comma separated" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
          <input value={form.languagesText} onChange={(event) => setForm((current) => ({ ...current, languagesText: event.target.value }))} placeholder="Languages, comma separated" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
          <input value={form.certificationsText} onChange={(event) => setForm((current) => ({ ...current, certificationsText: event.target.value }))} placeholder="Certifications, comma separated" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
          <input value={form.yearsExperience} onChange={(event) => setForm((current) => ({ ...current, yearsExperience: event.target.value }))} placeholder="Years of experience" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
          <input value={form.publicTagsText} onChange={(event) => setForm((current) => ({ ...current, publicTagsText: event.target.value }))} placeholder="Public tags, comma separated" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <label className="flex items-center gap-3 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.isPublic}
              onChange={(event) => setForm((current) => ({ ...current, isPublic: event.target.checked }))}
            />
            Publicly visible
          </label>
          <select
            value={form.profileStatus}
            onChange={(event) => setForm((current) => ({ ...current, profileStatus: event.target.value }))}
            className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        <div className="mt-6">
          <button
            onClick={handleSave}
            disabled={saving || !instructorUserId}
            data-testid="save-profile"
            className="rounded-2xl bg-slate-900 px-4 py-3 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save Public Profile'}
          </button>
        </div>
      </section>
    </div>
  );
}

export default InstructorPublicProfilePage;
